import SearchIcon from "@mui/icons-material/Search";
import GetAppIcon from "@mui/icons-material/GetApp";
import Add from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { customTableStyles, getBadge } from "components/shared/Tables/helper";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { devicesActions } from "../../../redux/slice/devices.slice";
import { uiActions } from "../../../redux/slice/ui.slice";
import { GET_DEVICES } from "../../../redux/types/devices.types";
import styles from "./secureBootHub.module.scss";
import CustomButton from "components/shared/Buttons/CustomButton";
import theme from "components/utils/theme";
import SecureBootInstallDialog from "components/modules/SingleDashboard/SecureBoot/SecureBootInstallDialog";
import { LoadingButton } from "@mui/lab";
import toast from "react-hot-toast";
import { setDeviceSecurebootServ } from "../../../redux/services/devices.service";
import { GET_SECUREBOOT_DEVICES } from "../../../redux/types/devices.types";

const SecureBootHub = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const devices = useSelector(devicesActions.selectors.getDevices);
  const securebootDeviceList = useSelector(
    devicesActions.selectors.getSecurebootDevices
  ) || [];

  const [installDialogOpen, setInstallDialogOpen] = useState(false);
  const [addDeviceDialogOpen, setAddDeviceDialogOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [isAddDeviceLoading, setIsAddDeviceLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(uiActions.actions.setSelectedNav("Secure Boot"));
  }, [dispatch]);

  useEffect(() => {
    dispatch({ type: GET_DEVICES });
  }, [dispatch]);


  const normalizedDevices = useMemo(() => {
    const deviceList = Array.isArray(devices) ? devices : [];
    return deviceList.filter((device) => device?.deviceType === "device");
  }, [devices]);

  const eligibleDevices = useMemo(() => {
    return normalizedDevices.filter(
      (device) => !Boolean(device?.securebootEnabled)
    );
  }, [normalizedDevices]);

  const totalDeviceCount = securebootDeviceList?.length || 0;

  const filteredDevices = useMemo(() => {
    if (!searchTerm) {
      return securebootDeviceList;
    }

    const regex = new RegExp(searchTerm, "i");

    return securebootDeviceList.filter((device) => {
      const fields = [
        device?.deviceName,
        device?.deviceId,
        device?.assetId,
        device?.systemDetails?.ip,
        device?.systemDetails?.os,
      ];

      return fields.some((value) =>
        typeof value === "string" ? regex.test(value) : false
      );
    });
  }, [searchTerm, securebootDeviceList]);

  useEffect(() => {
    dispatch({ type: GET_SECUREBOOT_DEVICES });
  }, [dispatch]);

  const handleViewUsers = useCallback(
    (device) => {
      if (!device) {
        return;
      }

      const identifier = device?._id || device?.deviceId;
      if (!identifier) {
        return;
      }

      dispatch(devicesActions.actions.setSelectedDevice(device));
      dispatch(devicesActions.actions.setCurrentDevice(device));

      navigate(`/secureboot/${identifier}/users`);
    },
    [dispatch, navigate]
  );

  const handleOpenInstallDialog = useCallback(() => {
    setInstallDialogOpen(true);
  }, []);

  const handleCloseInstallDialog = useCallback(() => {
    setInstallDialogOpen(false);
  }, []);

  const handleOpenAddDeviceDialog = useCallback(() => {
    setAddDeviceDialogOpen(true);
  }, []);

  const handleCloseAddDeviceDialog = useCallback(() => {
    setAddDeviceDialogOpen(false);
    setSelectedDeviceId("");
  }, []);

  const handleEnableSelectedDevice = useCallback(async () => {
    if (!selectedDeviceId) {
      toast.error("Select a device to enable SecureBoot");
      return;
    }

    setIsAddDeviceLoading(true);
    try {
      await setDeviceSecurebootServ({ deviceId: selectedDeviceId, enable: true });
      toast.success("SecureBoot enabled on selected device");
      dispatch({ type: GET_DEVICES });
      dispatch({ type: GET_SECUREBOOT_DEVICES });
      handleCloseAddDeviceDialog();
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to enable SecureBoot";
      toast.error(message);
    } finally {
      setIsAddDeviceLoading(false);
    }
  }, [dispatch, handleCloseAddDeviceDialog, selectedDeviceId]);

  const deviceRows = useMemo(() => {
    return filteredDevices.map((device) => {
      const identifier = device?._id || device?.deviceId || device?.id;
      const os = device?.systemDetails?.os || "Unknown";
      const status = device?.status || "Unknown";

      return {
        id: identifier,
        name:
          device?.deviceName ||
          device?.systemDetails?.hostname ||
          identifier ||
          "Device",
        identifier,
        os,
        status,
        raw: device,
      };
    });
  }, [filteredDevices]);

  const deviceColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Device",
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <Stack spacing={0.5}>
            <Typography variant="body1" fontWeight={600}>
              {params?.row?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params?.row?.identifier}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "os",
        headerName: "OS",
        width: 120,
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (params) => getBadge(params?.row?.status),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            variant="contained"
            size="small"
            onClick={() => handleViewUsers(params?.row?.raw)}
            sx={{ textTransform: "none" }}
          >
            View Users
          </Button>
        ),
      },
    ],
    [handleViewUsers]
  );

  return (
    <div className={styles.container}>
      <Card variant="outlined" className={styles.deviceCard}>
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack spacing={1}>
              <Typography variant="h5" fontWeight={600}>
                SecureBoot Devices ({totalDeviceCount})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pick a device below to view and manage its SecureBoot users.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                onClick={handleOpenAddDeviceDialog}
                startIcon={<Add />}
                variant="contained"
                size="small"
                sx={{
                  textTransform: "none",
                  backgroundColor: "#fff",
                  color: "#04112b",
                  border: "1px solid #000",
                  '&:hover': {
                    backgroundColor: "#fff",
                  },
                }}
              >
                Add Device
              </Button>
              <CustomButton
                onClick={handleOpenInstallDialog}
                label="Install"
                Icon={GetAppIcon}
                bgColor={theme.palette.primary.main}
                color={theme.palette.white.main}
                hover={theme.palette.primary.light}
              />
            </Stack>
          </Stack>

          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search devices"
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Box className={styles.tableWrapper}>
            <DataGrid
              rows={deviceRows}
              columns={deviceColumns}
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableDensitySelector
              hideFooter
              autoHeight
              rowHeight={60}
              sx={{
                ...customTableStyles,
                border: 0,
              }}
            />
          </Box>
          <Dialog
            open={addDeviceDialogOpen}
            onClose={handleCloseAddDeviceDialog}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Enable SecureBoot</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a registered device to add to SecureBoot. Only devices from
                the Devices view that are not yet enabled appear here.
              </Typography>
              <FormControl fullWidth margin="dense">
                <InputLabel id="secureboot-device-label">Device</InputLabel>
                <Select
                  labelId="secureboot-device-label"
                  value={selectedDeviceId}
                  label="Device"
                  onChange={(event) => setSelectedDeviceId(event.target.value)}
                  disabled={!eligibleDevices.length}
                >
                  {eligibleDevices.map((device) => (
                    <MenuItem
                      key={device?._id}
                      value={device?._id?.toString()}
                      sx={{
                        minWidth: 420,
                        flexDirection: "column",
                        alignItems: "flex-start",
                        py: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        {device?.deviceName || device?.deviceId || "Unnamed device"}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                        <Typography>{device?.deviceId}</Typography>
                        <Typography>• {device?.deviceType ?? "device"}</Typography>
                        <Typography>• {device?.systemDetails?.os || "OS unknown"}</Typography>
                      </Stack>
                      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                        Status: {device?.status || "Unknown"}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!eligibleDevices.length && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  All registered devices already have SecureBoot enabled.
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseAddDeviceDialog} disabled={isAddDeviceLoading}>
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                onClick={handleEnableSelectedDevice}
                loading={isAddDeviceLoading}
                disabled={!selectedDeviceId}
                sx={{ textTransform: "none" }}
              >
                Enable SecureBoot
              </LoadingButton>
            </DialogActions>
          </Dialog>
        </Stack>
      </Card>
      <SecureBootInstallDialog
        open={installDialogOpen}
        onClose={handleCloseInstallDialog}
      />
    </div>
  );
};

export default SecureBootHub;
